'use client'

import * as React from 'react'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { ArrowRight, Check, Heart, Instagram, MessageCircle, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appSignupUrl } from '@/lib/widget/embed'
import { demoPostsFor, getDemoProduct, type DemoPost } from '@/lib/copy/demo-products'

// Client preview for the Instagram-feed channel tool. A visitor sees a
// faithful sample IG feed (grid or reel rail) honouring an accent + a
// column control, with shoppable hotspot pins, then routes into the
// real builder or signs up. The feed is sample content for shape and
// styling, the real IG connects via OAuth in-app.

const ACCENTS: { value: string; label: string }[] = [
  { value: '#CDEB45', label: 'Lime' },
  { value: '#FF5C8A', label: 'Coral' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#111111', label: 'Ink' },
]

type Mode = 'grid' | 'reel'

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

export function InstagramFeedPreview() {
  const reducedMotion = usePrefersReducedMotion()
  const [accent, setAccent] = React.useState(ACCENTS[0].value)
  const [columns, setColumns] = React.useState(3)
  const [mode, setMode] = React.useState<Mode>('grid')

  const posts = React.useMemo(() => demoPostsFor('fashion'), [])
  const tiles = posts.concat(posts).slice(0, 9)
  const accentLabel = ACCENTS.find((a) => a.value === accent)?.label ?? accent

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-start">
      {/* Preview */}
      <div className="order-1 rounded-2xl border bg-card p-4 sm:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
            <Instagram className="h-4 w-4 text-primary" aria-hidden />
            @your.brand
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            <span className="relative flex h-1.5 w-1.5">
              <span className={cn('absolute inline-flex h-1.5 w-1.5 rounded-full bg-primary/70', !reducedMotion && 'animate-ping')} />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            Live sample
          </span>
        </div>

        {mode === 'reel' ? (
          <div className="overflow-hidden rounded-xl border bg-background p-3">
            <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {tiles.slice(0, 6).map((p, i) => (
                <div key={i} className="relative aspect-[9/16] w-28 shrink-0 overflow-hidden rounded-lg">
                  <Tile post={p} reducedMotion={reducedMotion} accent={accent} sizes="112px" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="rounded-xl border bg-background p-3"
            style={{ ['--igf-cols' as string]: columns, ['--igf-gap' as string]: '6px' } as React.CSSProperties}
          >
            <div className="igf-grid">
              {tiles.map((p, i) => (
                <div key={i} className="relative aspect-square overflow-hidden rounded-lg">
                  <Tile post={p} reducedMotion={reducedMotion} accent={accent} sizes="(min-width: 640px) 18vw, 30vw" />
                </div>
              ))}
            </div>
            <style>{`
              .igf-grid {
                display: grid;
                gap: var(--igf-gap);
                grid-template-columns: repeat(2, minmax(0, 1fr));
              }
              @media (min-width: 640px) {
                .igf-grid { grid-template-columns: repeat(var(--igf-cols), minmax(0, 1fr)); }
              }
            `}</style>
          </div>
        )}

        <p className="mt-3 text-xs text-muted-foreground">
          Sample content for shape and styling. Your real Instagram feed connects securely in-app via OAuth.
        </p>
      </div>

      {/* Controls + CTA */}
      <div className="order-2 lg:sticky lg:top-24">
        <div className="rounded-2xl border bg-card p-5 sm:p-6">
          <fieldset className="m-0 border-0 p-0">
            <legend className="font-display text-lg font-semibold tracking-tight text-foreground">Tune the look</legend>
            <p className="mt-1.5 text-sm text-muted-foreground">The preview updates as you go. You settle the look here, then wire the live feed when you publish.</p>

            <div className="mt-5 grid gap-5">
              <div role="group" aria-label="Feed shape" className="inline-flex w-fit rounded-full border bg-background p-0.5">
                <ModeBtn active={mode === 'grid'} onClick={() => setMode('grid')}>Grid</ModeBtn>
                <ModeBtn active={mode === 'reel'} onClick={() => setMode('reel')}>Reel rail</ModeBtn>
              </div>

              {mode === 'grid' && (
                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="igf-cols" className="text-sm font-medium text-foreground">Desktop columns</label>
                    <span className="text-sm tabular-nums text-muted-foreground">{columns}</span>
                  </div>
                  <input
                    id="igf-cols"
                    type="range"
                    min={2}
                    max={6}
                    value={columns}
                    onChange={(e) => setColumns(Number(e.target.value))}
                    className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-secondary accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              )}

              <div>
                <span className="block text-sm font-medium text-foreground" id="igf-accent-label">Accent</span>
                <div className="mt-2 flex flex-wrap gap-2" role="group" aria-labelledby="igf-accent-label">
                  {ACCENTS.map((a) => (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => setAccent(a.value)}
                      aria-pressed={accent === a.value}
                      aria-label={`Accent ${a.label}`}
                      className={cn(
                        'flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        accent === a.value ? 'border-primary text-foreground' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <span className="h-3.5 w-3.5 rounded-full border" style={{ backgroundColor: a.value }} aria-hidden />
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </fieldset>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              href="/tools/widget-builder?source=instagram"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            >
              Build it now <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
            <a
              href={appSignupUrl({ source: 'instagram', ref: 'tool-instagram-feed' })}
              className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium text-foreground transition-colors duration-150 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Connect Instagram in app
            </a>
          </div>
        </div>

        <p className="mt-4 inline-flex items-start gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
          You preview on sample posts here, accent {accentLabel}. Connect the real account via a quick OAuth so only the posts you approve go live.
        </p>
      </div>
    </div>
  )
}

function ModeBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

function Tile({ post, reducedMotion, accent, sizes }: { post: DemoPost; reducedMotion: boolean; accent: string; sizes: string }) {
  const hotspot = post.hotspots[0]
  const product = hotspot ? getDemoProduct(hotspot.productId) : null
  return (
    <>
      <ImgWithFallback
        src={post.image}
        alt={post.caption}
        fill
        sizes={sizes}
        className={cn('object-cover', !reducedMotion && 'transition-transform duration-500 ease-out hover:scale-[1.04]')}
      />
      {hotspot && (
        <span
          className="absolute flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-[9px] font-bold text-foreground shadow-md ring-2 ring-white/70"
          style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%`, backgroundColor: accent }}
          aria-hidden
          title={product?.name}
        >
          +
        </span>
      )}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/55 to-transparent p-1.5 text-[10px] font-medium text-white">
        <span className="inline-flex items-center gap-0.5"><Heart className="h-2.5 w-2.5" aria-hidden /> {post.likes}</span>
        <span className="inline-flex items-center gap-0.5"><MessageCircle className="h-2.5 w-2.5" aria-hidden /> {post.comments}</span>
      </span>
    </>
  )
}

export function InstagramFeedNote() {
  return (
    <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
      Auto-curation, product tagging and rights requests are all included. No source is gated behind a higher tier.
    </p>
  )
}
